import clsx from 'clsx';
import React, { InputHTMLAttributes, ReactNode, useState } from 'react';

import styles from './Input.module.scss';

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix' | 'size'> {
  /** Forwarded to the outer wrapper `<div>` (not the underlying `<input>`). */
  ref?: React.Ref<HTMLInputElement>;
  /** Height preset of the input element. Defaults to `'large'`. */
  size?: 'medium' | 'large';
  /** Element rendered inside the input on the leading edge (typically an `Icon`). */
  leftIcon?: ReactNode;
  /** Element rendered inside the input on the trailing edge (typically an `Icon` or clear button). */
  rightIcon?: ReactNode;
  /** When true, applies the error visual treatment and shows `errorText`. */
  hasError?: boolean;
  /** Message rendered below the input when `hasError` is true. Replaces `helperText` while in error state. */
  errorText?: ReactNode;
  /** Helper / hint text rendered below the input when not in error state. */
  helperText?: ReactNode;
  /** Right-align the typed value (useful for numeric inputs). Always on when `prefix` is set. */
  rightAlignText?: boolean;
  /** Disables the input and applies the muted visual treatment. */
  disabled?: boolean;
  /** Static content rendered inside the input before the value (e.g. currency symbol). Implicitly right-aligns the value. */
  prefix?: ReactNode;
  /** Visible label rendered above the input. Linked to the input via `htmlFor` when `id` is set. */
  label?: ReactNode;
  /** Test selector forwarded as `data-testid`. */
  'data-testid'?: string;
  /** Visual variant: `'fill'` (default) or `'outline'`. */
  variant?: 'fill' | 'outline';
  /** Number of rows when rendered as a textarea (`as="textarea"`). */
  rows?: number;
  /** Render as a single-line `<input>` (default) or multi-line `<textarea>`. */
  as?: 'input' | 'textarea';
}

/**
 * Single-line text input (or multi-line textarea via `as="textarea"`)
 * with optional label, leading/trailing icons, prefix, helper text,
 * and error state.
 *
 * The component is uncontrolled or controlled depending on the standard
 * `value` / `defaultValue` / `onChange` props it receives — these are
 * forwarded to the native input via spread.
 */
export const Input = ({
  size = 'large',
  leftIcon,
  rightIcon,
  hasError,
  errorText,
  helperText,
  rightAlignText,
  label,
  id,
  className,
  prefix,
  disabled = false,
  variant = 'fill',
  rows,
  as: Component = 'input',
  ref,
  ...rest
}: InputProps) => {
  const [focused, setFocused] = useState(false);

  const onFocus = () => {
    setFocused(true);
  };

  const onBlur = () => {
    setFocused(false);
  };

  return (
    <div
      data-input-container
      ref={ref}
      className={clsx(styles.container, className)}
      data-testid="input-container"
    >
      {label && (
        <label data-input-label htmlFor={id} className={styles.styledLabel}>
          {label}
        </label>
      )}
      <div
        data-input-wrapper
        onFocus={onFocus}
        onBlur={onBlur}
        data-testid="input-wrapper"
        className={clsx(styles.inputWrapper, styles[variant], {
          [styles.error]: hasError,
          [styles.focused]: focused,
          [styles.isDisabled]: disabled,
        })}
      >
        {leftIcon && (
          <span data-input-left-icon className={styles.leftIcon}>
            {leftIcon}
          </span>
        )}
        {prefix && (
          <div data-input-prefix className={styles.prefixContainer}>
            {prefix}
          </div>
        )}
        <input
          data-input-element
          className={clsx(styles.inputElement, styles[size], {
            [styles.rightAlignText]: rightAlignText || !!prefix,
            [styles.disabled]: disabled,
          })}
          id={id}
          {...(rows ? { rows } : {})}
          disabled={disabled}
          {...rest}
        />
        {rightIcon && (
          <span data-input-right-icon className={styles.rightIcon}>
            {rightIcon}
          </span>
        )}
      </div>
      {hasError && errorText && (
        <label
          data-input-error
          data-testid="error-text"
          className={styles.errorLabel}
        >
          {errorText}
        </label>
      )}
      {!hasError && helperText && (
        <label
          data-input-helper
          data-testid="helper-text"
          className={styles.helperLabel}
        >
          {helperText}
        </label>
      )}
    </div>
  );
};
